import { useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from 'blick.react.utils';
import type { WebSocketMessage } from 'blick.react.utils';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

// экспоненциальный backoff: 1, 2, 4, 8, ... capped 30s + jitter ±20%
const getReconnectDelay = (attempt: number) => {
    const exponential = Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt), RECONNECT_MAX_DELAY_MS);
    const jitter = exponential * (0.8 + Math.random() * 0.4);
    return Math.min(Math.round(jitter), RECONNECT_MAX_DELAY_MS);
}

export interface IReconnectingWebSocketOptions {
    onConnected?: (clientId: string) => void;
    onMessage?: (message: WebSocketMessage) => void;
    onError?: (error: Event) => void;
    onDisconnected?: (event: CloseEvent) => void;
    /** false — не подключаться и не переподключаться */
    enabled?: boolean;
}

interface IReconnectingWebSocketReturn {
    isConnected: boolean;
    connect: () => void;
    disconnect: () => void;
}

// Обёртка над useWebSocket (blick.react.utils): добавляет авто-reconnect с экспоненциальной задержкой.
// Базовый хук подключается один раз при монтировании и при обрыве не переподключается.
const useReconnectingWebSocket = (getUrl: () => string, options: IReconnectingWebSocketOptions = {}): IReconnectingWebSocketReturn => {
    const { enabled = true } = options;

    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const reconnectAttemptRef = useRef(0);
    const manualDisconnectRef = useRef(false);
    const enabledRef = useRef(enabled);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        enabledRef.current = enabled;
        if (!enabled && reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = undefined;
        }
    }, [enabled]);

    const clearReconnectTimer = () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = undefined;
        }
    };

    const scheduleReconnect = () => {
        if (!enabledRef.current || manualDisconnectRef.current)
            return;
        if (reconnectTimeoutRef.current)
            return;
        const delay = getReconnectDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = undefined;
            connectRef.current?.();
        }, delay);
    };

    const { isConnected, connect, disconnect } = useWebSocket(getUrl(), {
        onConnected: (clientId) => {
            reconnectAttemptRef.current = 0;
            manualDisconnectRef.current = false;
            clearReconnectTimer();
            optionsRef.current.onConnected?.(clientId);
        },
        onMessage: (m) => optionsRef.current.onMessage?.(m),
        onError: (error) => {
            optionsRef.current.onError?.(error);
            scheduleReconnect();
        },
        onDisconnected: (event) => {
            optionsRef.current.onDisconnected?.(event);
            // код 1000 — нормальное закрытие (наш disconnect/unmount), цикл не запускаем
            if (event.code === 1000)
                return;
            scheduleReconnect();
        },
    });

    const connectRef = useRef(connect);
    connectRef.current = connect;

    // пересборка URL при каждом подключении (актуальный JWT) и реакция на enabled
    useEffect(() => {
        manualDisconnectRef.current = false;
        if (enabled)
            connectRef.current?.();
    }, [getUrl, enabled]);

    const manualDisconnect = useCallback(() => {
        manualDisconnectRef.current = true;
        clearReconnectTimer();
        disconnect(1000, 'Manual disconnect');
    }, [disconnect]);

    useEffect(() => () => clearReconnectTimer(), []);

    return { isConnected, connect, disconnect: manualDisconnect };
};

export default useReconnectingWebSocket;